import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  EnvelopeOpen, Buildings, GoogleLogo, ArrowUpRight, WarningCircle, CheckCircle, Clock,
} from "@phosphor-icons/react";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invitations/lookup?token=${encodeURIComponent(token)}`);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || "Invite unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const startGoogle = () => {
    // Stash the invite token so the AuthCallback can present it to the backend
    sessionStorage.setItem("apexgrey_invite_token", token);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const acceptSignedIn = async () => {
    setAccepting(true);
    try {
      await api.post("/invitations/accept", { token });
      await refresh();
      toast.success(`Joined ${preview?.org_name}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invite");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-graphite-950 text-ink-primary px-6" data-testid="invite-page">
      <div className="w-full max-w-md rounded-md border border-white/10 bg-graphite-900 p-8 md:p-10">
        {loading ? (
          <div className="text-center text-sm text-ink-secondary py-10">Loading invite…</div>
        ) : error ? (
          <div className="text-center" data-testid="invite-error">
            <div className="mx-auto h-12 w-12 rounded-full border border-red-400/30 bg-red-400/5 grid place-items-center">
              <WarningCircle weight="duotone" className="h-6 w-6 text-red-300" />
            </div>
            <div className="mt-4 font-display text-2xl tracking-tight">Invite unavailable</div>
            <p className="mt-2 text-sm text-ink-secondary">{error}</p>
            <button
              onClick={() => navigate("/login")}
              data-testid="invite-error-back"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm hover:bg-white/5"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ink-secondary">
              <EnvelopeOpen weight="duotone" className="h-4 w-4" />
              You&rsquo;ve been invited
            </div>
            <h1 className="mt-3 font-display text-3xl md:text-4xl font-light tracking-tight">
              Join <span className="text-ink-primary" data-testid="invite-org-name">{preview?.org_name}</span>
            </h1>
            <div className="mt-4 rounded-md border border-white/10 bg-graphite-800 p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-ink-secondary">
                <Buildings weight="duotone" className="h-4 w-4" />
                <span>Role: <span className="text-ink-primary capitalize" data-testid="invite-role">{preview?.role}</span></span>
              </div>
              <div className="flex items-center gap-2 text-ink-secondary">
                <EnvelopeOpen weight="duotone" className="h-4 w-4" />
                <span>Sent to <span className="text-ink-primary" data-testid="invite-email">{preview?.email}</span></span>
              </div>
              {preview?.invited_by_name && (
                <div className="flex items-center gap-2 text-ink-secondary">
                  <CheckCircle weight="duotone" className="h-4 w-4" />
                  <span>Invited by <span className="text-ink-primary">{preview.invited_by_name}</span></span>
                </div>
              )}
              <div className="flex items-center gap-2 text-ink-secondary">
                <Clock weight="duotone" className="h-4 w-4" />
                <span className="text-[11px]">Expires soon</span>
              </div>
            </div>

            {user ? (
              user.email?.toLowerCase() === (preview?.email || "").toLowerCase() ? (
                <button
                  onClick={acceptSignedIn}
                  disabled={accepting}
                  data-testid="invite-accept-signed-in"
                  className="mt-6 w-full inline-flex items-center justify-between rounded-full bg-white text-graphite-950 px-6 py-3.5 font-medium hover:bg-neutral-100 disabled:opacity-60"
                >
                  <span>{accepting ? "Joining…" : `Accept & join ${preview?.org_name}`}</span>
                  <ArrowUpRight weight="bold" className="h-4 w-4" />
                </button>
              ) : (
                <div className="mt-6 text-sm text-amber-200/90 bg-amber-400/5 border border-amber-400/25 rounded-md p-3" data-testid="invite-email-mismatch">
                  This invite was sent to <span className="font-medium">{preview?.email}</span>, but you&rsquo;re signed in as <span className="font-medium">{user.email}</span>. Sign out and click the invite link again from the correct Google account.
                </div>
              )
            ) : (
              <button
                onClick={startGoogle}
                data-testid="invite-google-button"
                className="mt-6 w-full inline-flex items-center justify-between rounded-full bg-white text-graphite-950 px-6 py-3.5 font-medium hover:bg-neutral-100"
              >
                <span className="inline-flex items-center gap-3">
                  <GoogleLogo weight="bold" className="h-5 w-5" />
                  Continue with Google
                </span>
                <ArrowUpRight weight="bold" className="h-4 w-4" />
              </button>
            )}
            <p className="mt-4 text-[11px] text-ink-secondary/70 text-center">
              Signing in creates your account and lands you directly inside {preview?.org_name}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
